import { useState, useRef, useEffect, useCallback } from "react";

// ─── Canvas constants (all in logical pixels; drawn at S px each) ────────────
const S    = 2;    // scale factor: 1 logical px → S canvas px
const CW   = 320;  // logical canvas width
const CH   = 90;   // logical canvas height
const GND  = 77;   // ground y (logical)
const GRAV = 0.65; // gravity per frame
const JV   = -7;   // jump velocity (negative = up)
const PX   = 30;   // player x (fixed)
const PW   = 12;   // player collision width
const PH   = 16;   // player collision height (12 body + 4 legs)
const OW   = 8;    // obstacle width

const OBS_TYPES = [
  { label: "W-2",   h: 13 },
  { label: "1040",  h: 15 },
  { label: "K-1",   h: 11 },
  { label: "AUDIT", h: 15 },
  { label: "TPS",   h: 11 },
];

// Pre-computed background "star" positions (deterministic — no per-frame RNG)
const BG_DOTS = Array.from({ length: 18 }, (_, i) => ({
  x: Math.floor((i * 53 + 19) % (CW - 4)) + 2,
  y: Math.floor((i * 37 + 11) % (GND - 12)) + 2,
}));

// ─── Round-rect helper (with fallback for older browsers) ─────────────────────
function fillRoundRect(ctx, x, y, w, h, r) {
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    ctx.fill();
  } else {
    ctx.fillRect(x + r, y,     w - 2 * r, h);
    ctx.fillRect(x,     y + r, r,         h - 2 * r);
    ctx.fillRect(x + w - r, y + r, r, h - 2 * r);
  }
}

// ─── Draw Kieth Botfield — round green blob with walrus mustache ──────────────
function drawKieth(ctx, lx, ly, frame, jumping) {
  const x = Math.floor(lx) * S;
  const y = Math.floor(ly) * S;

  // ── Antennae (drawn first so body overlaps their base) ─────────────────────
  ctx.fillStyle = "#16a34a";
  // Left stalk (leans slightly left)
  ctx.fillRect(x + 3 * S, y - 4 * S, S, 5 * S);
  ctx.fillRect(x + 2 * S, y - 6 * S, S, 3 * S);
  // Right stalk (leans slightly right)
  ctx.fillRect(x + 8 * S, y - 4 * S, S, 5 * S);
  ctx.fillRect(x + 9 * S, y - 6 * S, S, 3 * S);
  // Bobbles (bright green)
  ctx.fillStyle = "#22c55e";
  ctx.fillRect(x + S,      y - 7 * S, 2 * S, 2 * S); // left bobble
  ctx.fillRect(x + 9 * S,  y - 7 * S, 2 * S, 2 * S); // right bobble

  // ── Round body (12×12 logical) ─────────────────────────────────────────────
  // Dark outline first (slightly larger)
  ctx.fillStyle = "#14532d";
  fillRoundRect(ctx, x - S, y - S, 14 * S, 14 * S, 4 * S);

  // Bright green fill
  ctx.fillStyle = "#22c55e";
  fillRoundRect(ctx, x, y, 12 * S, 12 * S, 3 * S);

  // Right/bottom shading for subtle 3-D depth
  ctx.fillStyle = "#16a34a";
  fillRoundRect(ctx, x + 6 * S, y + 2 * S, 5 * S, 9 * S, 2 * S);

  // Re-fill left half bright so shading only shows on right
  ctx.fillStyle = "#22c55e";
  fillRoundRect(ctx, x, y, 8 * S, 12 * S, 3 * S);

  // ── Eyes (2×2 white + 1×1 dark pupil) ─────────────────────────────────────
  ctx.fillStyle = "#f0fdf4";
  ctx.fillRect(x + 2 * S, y + 2 * S, 2 * S, 2 * S); // left eye
  ctx.fillRect(x + 8 * S, y + 2 * S, 2 * S, 2 * S); // right eye
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(x + 3 * S, y + 3 * S, S, S);           // left pupil
  ctx.fillRect(x + 9 * S, y + 3 * S, S, S);           // right pupil

  // ── Walrus/handlebar mustache (white, prominent) ───────────────────────────
  ctx.fillStyle = "#f0fdf4";
  // Center horizontal bar
  ctx.fillRect(x + 4 * S, y + 6 * S, 4 * S, S);
  // Left arm (extends left, dips down)
  ctx.fillRect(x + 2 * S, y + 6 * S, 2 * S, S);
  ctx.fillRect(x + S,     y + 7 * S, 2 * S, S);
  ctx.fillRect(x + S,     y + 8 * S, S,     S);  // left tip
  // Right arm (mirror)
  ctx.fillRect(x + 8 * S, y + 6 * S, 2 * S, S);
  ctx.fillRect(x + 9 * S, y + 7 * S, 2 * S, S);
  ctx.fillRect(x + 10 * S, y + 8 * S, S,    S);  // right tip

  // ── Stubby legs (medium green) ─────────────────────────────────────────────
  ctx.fillStyle = "#16a34a";
  if (jumping) {
    ctx.fillRect(x + 3 * S, y + 12 * S, 2 * S, 2 * S);
    ctx.fillRect(x + 7 * S, y + 12 * S, 2 * S, 2 * S);
  } else if (frame === 0) {
    ctx.fillRect(x + 2 * S, y + 12 * S, 2 * S, 3 * S); // left leg forward
    ctx.fillRect(x + 8 * S, y + 12 * S, 2 * S, 2 * S); // right leg back
  } else {
    ctx.fillRect(x + 2 * S, y + 12 * S, 2 * S, 2 * S); // left leg back
    ctx.fillRect(x + 8 * S, y + 12 * S, 2 * S, 3 * S); // right leg forward
  }

  // ── Teal shoes ─────────────────────────────────────────────────────────────
  ctx.fillStyle = "#0d9488";
  if (jumping) {
    ctx.fillRect(x + 2 * S, y + 14 * S, 3 * S, S);
    ctx.fillRect(x + 7 * S, y + 14 * S, 3 * S, S);
  } else if (frame === 0) {
    ctx.fillRect(x + S,     y + 15 * S, 4 * S, S); // left foot forward
    ctx.fillRect(x + 7 * S, y + 14 * S, 3 * S, S); // right foot back
  } else {
    ctx.fillRect(x + S,     y + 14 * S, 3 * S, S); // left foot back
    ctx.fillRect(x + 7 * S, y + 15 * S, 4 * S, S); // right foot forward
  }
}

// ─── Render one game frame ─────────────────────────────────────────────────
function renderFrame(ctx, g) {
  const W = CW * S;
  const H = CH * S;

  // Dark green background
  ctx.fillStyle = "#071a0e";
  ctx.fillRect(0, 0, W, H);

  // Subtle CRT scanlines
  ctx.fillStyle = "rgba(0,0,0,0.14)";
  for (let scanY = 0; scanY < H; scanY += 2 * S) {
    ctx.fillRect(0, scanY, W, S);
  }

  // Background dots (static "stars")
  ctx.fillStyle = "#0f4a20";
  for (const d of BG_DOTS) {
    ctx.fillRect(d.x * S, d.y * S, S, S);
  }

  // Collision flash (red tint)
  if (g.gameOver && g.flashTimer > 0) {
    ctx.fillStyle = `rgba(220,38,38,${(g.flashTimer / 20) * 0.5})`;
    ctx.fillRect(0, 0, W, H);
  }

  // Ground (dark base + bright green top highlight)
  ctx.fillStyle = "#0a3018";
  ctx.fillRect(0, GND * S, W, (CH - GND) * S);
  ctx.fillStyle = "#22c55e";
  ctx.fillRect(0, GND * S, W, S); // bright green top line

  // Obstacles
  for (const o of g.obstacles) {
    const ox = Math.floor(o.x) * S;
    const oy = (GND - o.h) * S;
    // Main block
    ctx.fillStyle = "#0d9488";
    ctx.fillRect(ox, oy, OW * S, o.h * S);
    // Left shadow
    ctx.fillStyle = "#134e4a";
    ctx.fillRect(ox, oy, S, o.h * S);
    // Bottom shadow
    ctx.fillRect(ox, oy + o.h * S - S, OW * S, S);
    // Label above (bright green text)
    ctx.fillStyle = "#22c55e";
    ctx.font = `bold ${5 * S}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText(o.label, ox + (OW * S) / 2, oy - 2 * S);
  }

  // Player
  const isJumping = g.player.jumping || g.player.vy < -0.1;
  drawKieth(ctx, PX, g.player.y, g.player.frame, isJumping);

  // Score (top right, bright green)
  ctx.fillStyle = "#22c55e";
  ctx.font = `bold ${6 * S}px monospace`;
  ctx.textAlign = "right";
  ctx.fillText(String(g.score), (CW - 4) * S, 9 * S);

  // "LOADING..." blink at bottom
  if (!g.dataReady) {
    const blink = Math.floor(g.tick / 22) % 2 === 0;
    ctx.fillStyle = blink ? "#22c55e" : "#0f4a20";
    ctx.font = `${5 * S}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText("LOADING DATA\u2026", (CW / 2) * S, (CH - 4) * S);
  }

  // "DATA READY!" overlay — dark backdrop + blinking text
  if (g.dataReady) {
    ctx.fillStyle = "rgba(7,26,14,0.88)";
    ctx.fillRect(0, 0, W, H);
    const blink = Math.floor(g.readyTimer / 6) % 2 === 0;
    if (blink) {
      ctx.fillStyle = "#22c55e";
      ctx.font = `bold ${10 * S}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText("DATA READY!", W / 2, H / 2 + 4 * S);
    }
  }
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function LoadingGame({ ready }) {
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(true);

  const gameRef = useRef({
    player: { y: GND - PH, vy: 0, jumping: false, frame: 0, frameTimer: 0 },
    obstacles: [],
    score: 0,
    speed: 1.6,
    spawnTimer: 70,
    tick: 0,
    gameOver: false,
    flashTimer: 0,
    dataReady: false,
    readyTimer: 0,
    raf: null,
  });

  // Bridge ready prop → game loop ref (runs after each render where ready changes)
  useEffect(() => {
    if (ready) gameRef.current.dataReady = true;
  }, [ready]);

  // Jump: only when grounded and not in game-over flash
  const jump = useCallback(() => {
    const g = gameRef.current;
    if (!g.gameOver && !g.player.jumping && g.player.vy >= 0) {
      g.player.vy = JV;
      g.player.jumping = true;
    }
  }, []);

  // Game loop — runs once on mount, RAF-driven, no React state reads inside
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    const g = gameRef.current;

    function loop() {
      g.tick++;

      // ── Data ready sequence ──────────────────────────────────────────────
      if (g.dataReady) {
        g.readyTimer++;
        renderFrame(ctx, g);
        if (g.readyTimer > 66) {
          setVisible(false); // safe in React 18 even after unmount
          return;            // stop loop (no re-schedule)
        }
        g.raf = requestAnimationFrame(loop);
        return;
      }

      // ── Game over: flash then reset ──────────────────────────────────────
      if (g.gameOver) {
        g.flashTimer--;
        if (g.flashTimer <= 0) {
          g.gameOver       = false;
          g.player.y       = GND - PH;
          g.player.vy      = 0;
          g.player.jumping = false;
          g.obstacles      = [];
          g.speed          = 1.6;
          g.spawnTimer     = 70;
        }
        renderFrame(ctx, g);
        g.raf = requestAnimationFrame(loop);
        return;
      }

      // ── Physics ───────────────────────────────────────────────────────────
      g.speed += 0.0012;

      g.player.vy += GRAV;
      g.player.y  += g.player.vy;
      if (g.player.y >= GND - PH) {
        g.player.y       = GND - PH;
        g.player.vy      = 0;
        g.player.jumping = false;
      }

      // Walk cycle: toggle frame every 8 ticks
      if (++g.player.frameTimer >= 8) {
        g.player.frame      = (g.player.frame + 1) % 2;
        g.player.frameTimer = 0;
      }

      // Obstacle spawning
      if (--g.spawnTimer <= 0) {
        const type = OBS_TYPES[Math.floor(Math.random() * OBS_TYPES.length)];
        g.obstacles.push({ x: CW + 8, h: type.h, label: type.label, scored: false });
        g.spawnTimer = 72 + Math.floor(Math.random() * 52);
      }

      // Move + score when obstacle passes player
      for (const o of g.obstacles) {
        o.x -= g.speed;
        if (!o.scored && o.x + OW < PX) { o.scored = true; g.score++; }
      }
      g.obstacles = g.obstacles.filter(o => o.x + OW > -8);

      // Collision detection (3px inset for forgiving hitbox)
      const inset = 3;
      const py    = g.player.y;
      for (const o of g.obstacles) {
        if (
          PX + PW - inset > o.x + inset       &&
          PX + inset       < o.x + OW - inset  &&
          py + PH - inset  > GND - o.h         &&
          py + inset       < GND
        ) {
          g.gameOver   = true;
          g.flashTimer = 20;
          break;
        }
      }

      renderFrame(ctx, g);
      g.raf = requestAnimationFrame(loop);
    }

    g.raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(g.raf); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Space / Enter to jump
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "Enter" || e.key === " ") {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jump]);

  if (!visible) return null;

  return (
    <div className="loading-game-overlay">
      <div className="loading-game-title">RIGHT PEOPLE. RIGHT SEATS.</div>
      <canvas
        ref={canvasRef}
        width={CW * S}
        height={CH * S}
        className="loading-game-canvas"
        onClick={jump}
      />
      <div className="loading-game-hint">SPACE or CLICK to jump</div>
    </div>
  );
}
